/**
 * Dots & Boxes Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum DotsBoxesErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface DotsBoxesError {
  type: DotsBoxesErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class DotsBoxesErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: DotsBoxesError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: DotsBoxesErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): DotsBoxesError {
    const error: DotsBoxesError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'DotsAndBoxes', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: DotsBoxesErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case DotsBoxesErrorType.INVALID_MOVE:
      case DotsBoxesErrorType.NOT_YOUR_TURN:
      case DotsBoxesErrorType.GAME_ENDED:
      case DotsBoxesErrorType.GAME_LOGIC:
      case DotsBoxesErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case DotsBoxesErrorType.CONNECTION_ERROR:
      case DotsBoxesErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case DotsBoxesErrorType.CORRUPTED_DATA:
      case DotsBoxesErrorType.SAVE_ERROR:
      case DotsBoxesErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): DotsBoxesError {
    return this.handleError(
      DotsBoxesErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleInvalidMove(line: string): DotsBoxesError {
    return this.handleError(
      DotsBoxesErrorType.INVALID_MOVE,
      `Invalid move: ${line}. That line has already been drawn.`,
      { line }
    );
  }

  handleNotYourTurn(): DotsBoxesError {
    return this.handleError(
      DotsBoxesErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): DotsBoxesError {
    return this.handleError(
      DotsBoxesErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleSaveError(gameId: string, error: any): DotsBoxesError {
    return this.handleError(
      DotsBoxesErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): DotsBoxesError {
    return this.handleError(
      DotsBoxesErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: DotsBoxesErrorType): boolean {
    switch (type) {
      case DotsBoxesErrorType.INVALID_MOVE:
      case DotsBoxesErrorType.NOT_YOUR_TURN:
      case DotsBoxesErrorType.GAME_ENDED:
      case DotsBoxesErrorType.GAME_LOGIC:
      case DotsBoxesErrorType.CONNECTION_ERROR:
      case DotsBoxesErrorType.CORRUPTED_DATA:
      case DotsBoxesErrorType.AI_TIMEOUT:
      case DotsBoxesErrorType.SAVE_ERROR:
      case DotsBoxesErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: DotsBoxesError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: DotsBoxesError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Dots & Boxes Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): DotsBoxesError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
